/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { FlexContainer, Tag } from "@nordcloud/gnui";
import { RoleBindingByUser } from "~/generated/graphql";
import { DateCell } from "~/components/Tables/cells/DateCell";
import {
  ActionsColumn,
  FlexColumnLarge,
  FlexColumn,
  MediumColumn,
  SmallColumn,
} from "~/components/Tables/styles";
import { dateFormat } from "~/constants";
import { isRoleEditable } from "~/views/permissions/utils";
import { Actions } from "./Actions";

const columnHelper = createColumnHelper<RoleBindingByUser>();

const LOGIN_STATUS = {
  UNDEFINED: "Undefined",
  DEFINED: "Defined",
  UNKNOWN: "Unknown",
} as const;

const UNKNOWN_LAST_LOGIN_TIMESTAMP = "1970-01-01T00:00:00.000+00:00";

const getLoginStatus = (lastLoggedInAt: string | undefined | null) => {
  if (!lastLoggedInAt) {
    return LOGIN_STATUS.UNDEFINED;
  }

  if (dayjs(lastLoggedInAt).isSame(dayjs(UNKNOWN_LAST_LOGIN_TIMESTAMP))) {
    return LOGIN_STATUS.UNKNOWN;
  }

  return LOGIN_STATUS.DEFINED;
};

type Props = {
  refetchUsers: () => void;
};

export function usersColumns({ refetchUsers }: Props) {
  return [
    columnHelper.accessor((row) => row?.subject, {
      header: "User Name",
      cell: (cell) => (
        <FlexContainer justifyContent="flex-start" alignItems="center">
          {cell.getValue()}
        </FlexContainer>
      ),
      meta: FlexColumn,
    }),
    columnHelper.accessor((row) => row?.roles, {
      header: "Roles",
      cell: ({ row }) => (
        <div>
          {row.original.roles?.map((role) => (
            <div key={role?.id}>
              <Tag text={role?.name} />
            </div>
          ))}
        </div>
      ),
      meta: FlexColumnLarge,
    }),
    columnHelper.accessor((row) => row?.roles, {
      header: "Added",
      cell: ({
        row: {
          original: {
            changeDetails: { createdAt },
          },
        },
      }) => <div>{dayjs(createdAt).format(dateFormat.dayMonthYear)}</div>,
      meta: MediumColumn,
    }),
    columnHelper.accessor((row) => row?.roles, {
      header: "Updated",
      cell: ({
        row: {
          original: {
            changeDetails: { updatedAt },
          },
        },
      }) => <div>{dayjs(updatedAt).format(dateFormat.dayMonthYear)}</div>,
      meta: MediumColumn,
    }),
    columnHelper.accessor((row) => row?.lastLoggedInAt, {
      header: "Last Login Date",
      cell: (cell) => {
        const lastLoggedInAt = cell.getValue();
        const loginStatus = getLoginStatus(lastLoggedInAt);
        if (loginStatus === LOGIN_STATUS.UNDEFINED) {
          return <div>Never Logged In</div>;
        }
        if (loginStatus === LOGIN_STATUS.UNKNOWN) {
          return <div>Unknown</div>;
        }
        return <DateCell value={lastLoggedInAt} />;
      },
      meta: SmallColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        const roleIds = row.original.roles.map((r) => r.id);
        const systemRoleNames = row.original.roles
          .filter((role) => !isRoleEditable(role))
          .map((role) => role.name);

        return (
          <FlexContainer justifyContent="space-around">
            <Actions
              roleIds={roleIds}
              email={row.original.subject}
              systemRoleNames={systemRoleNames}
              refetchUsers={refetchUsers}
            />
          </FlexContainer>
        );
      },
      meta: ActionsColumn,
    }),
  ] as never;
}
